import React, { createContext, useContext, useState, useEffect } from 'react';
import { updateHtmlLang } from '../utils/htmlLang';

type Language = 'en' | 'de';

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export const LanguageProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [language, setLanguage] = useState<Language>(() => {
    const pathLang = window.location.pathname.split('/')[1];
    return pathLang === 'de' ? 'de' : 'en';
  });

  // Update language when URL changes
  useEffect(() => {
    const handleUrlChange = () => {
      const pathLang = window.location.pathname.split('/')[1];
      setLanguage(pathLang === 'de' ? 'de' : 'en');
    };

    window.addEventListener('popstate', handleUrlChange);
    return () => window.removeEventListener('popstate', handleUrlChange);
  }, []);

  // Update HTML lang attribute when language changes
  useEffect(() => {
    updateHtmlLang(language);
  }, [language]);

  return (
    <LanguageContext.Provider value={{ language, setLanguage }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) throw new Error('useLanguage must be used within LanguageProvider');
  return context;
};